import type { CSSProperties, Dispatch, ReactNode, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import BaseButton from "../../components/BaseButton/BaseButton";
import { useNavigationHeader } from "../../components/MainLayout";
import { useAppData } from "../../data/AppDataProvider";
import styles from "./Cabinet.module.css";

type AppearStyle = CSSProperties & {
    "--appear-delay": string;
};

type CabinetData = {
    user_id: number;
    email: string | null;
    tg_id: number | null;
    ref_link: string;
    ref_type: "days" | "money";
    ref_percent: number;
    ref_total: number;
    ref_balance: number;
    ref_added: number;
    ref_active_added: number;
    days_balance: number;
};

type CabinetStatus = {
    isLoading: boolean;
    error?: string;
};

type CabinetAppData = {
    cabinetData: CabinetData;
    cabinetStatus: CabinetStatus;
    setCabinetData: Dispatch<SetStateAction<CabinetData>>;
};

type UserRow = {
    iconUrl: string;
    label: string;
    value: ReactNode;
    action: ReactNode;
};

const REF_TEXT_ICON_ASPECT_RATIO = 574 / 800;
const REF_TEXT_ICON_HEIGHT_OFFSET = 5;

function getAppearStyle(): AppearStyle {
    return {
        "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s`,
    };
}

function classNames(...classes: Array<string | false | null | undefined>): string {
    return classes.filter(Boolean).join(" ");
}

function CredentialsBlock({
    cabinetData,
    isLoading,
}: {
    cabinetData: CabinetData;
    isLoading: boolean;
}) {
    const navigate = useNavigate();

    const telegramBindUrl = useMemo(() => {
        if (!cabinetData.user_id) {
            return "";
        }
        return `https://t.me/sodavpn_bot?start=add_cred:${cabinetData.user_id}`;
    }, [cabinetData.user_id]);

    const userRows: UserRow[] = [
        {
            iconUrl: "/svg/mail.svg",
            label: "Почта",
            value: isLoading ? "Загрузка..." : cabinetData.email,
            action: !isLoading && !cabinetData.email ? (
                <button className={styles.bindButton} onClick={() => navigate("/cabinet/link-email")} type="button">
                    Привязать
                </button>
            ) : null,
        },
        {
            iconUrl: "/svg/telegram.svg",
            label: "Telegram",
            value: isLoading ? "Загрузка..." : (cabinetData.tg_id || ""),
            action: !isLoading && !cabinetData.tg_id ? (
                <button
                    className={styles.bindButton}
                    disabled={!telegramBindUrl}
                    onClick={() => {
                        if (telegramBindUrl) {
                            window.location.href = telegramBindUrl;
                        }
                    }}
                    type="button"
                >
                    Привязать
                </button>
            ) : null,
        },
    ];

    return (
        <div className={`default_block menu-appear ${styles.userRows}`} style={getAppearStyle()}>
            {userRows.map((row) => (
                <div key={row.label} className={`flex-between align-center gap1 ${styles.userRow}`}>
                    <div className={`flex-between align-center gap1 w100 ${styles.userRowContent}`}>
                        <span className={`font09 ${styles.userLabel}`}>
                            <span
                                className={styles.userIconBadge}
                                aria-hidden="true"
                            >
                                <span
                                    className={`${styles.userIcon} ${
                                        row.iconUrl.includes("telegram") ? styles.userIconTelegram : ""
                                    }`}
                                    style={{
                                        WebkitMask: `url(${row.iconUrl}) center / contain no-repeat`,
                                        mask: `url(${row.iconUrl}) center / contain no-repeat`,
                                    }}
                                />
                            </span>
                            {row.label}
                        </span>
                        {row.value ? <span className={styles.userValue}>{row.value}</span> : null}
                    </div>
                    {row.action}
                </div>
            ))}
        </div>
    );
}

function RefBlock({
    cabinetData,
    isLoading,
}: {
    cabinetData: CabinetData;
    isLoading: boolean;
}) {
    const isMoneyReferral = cabinetData.ref_type === "money";
    const referralContentRef = useRef<HTMLDivElement | null>(null);
    const [referralTextIconHeight, setReferralTextIconHeight] = useState<number | null>(null);

    useEffect(() => {
        if (isMoneyReferral) {
            return undefined;
        }

        const contentElement = referralContentRef.current;
        if (!contentElement) {
            return undefined;
        }

        const updateIconHeight = () => {
            setReferralTextIconHeight(
                Math.max(0, Math.ceil(contentElement.getBoundingClientRect().height) - REF_TEXT_ICON_HEIGHT_OFFSET),
            );
        };

        updateIconHeight();

        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(updateIconHeight);
        resizeObserver.observe(contentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isMoneyReferral]);

    const copyToClipboard = () => {
        if (!cabinetData.ref_link) {
            return;
        }

        void navigator.clipboard.writeText(cabinetData.ref_link);
    };

    return (
        <div className={`default_block default_block_padded menu-appear ${styles.referralCard}`} style={getAppearStyle()}>
            <div className={styles.referralHero}>
                <div ref={referralContentRef} className={styles.referralHeader}>
                    <span className={styles.referralTitle}>
                        <span
                            className={styles.referralIcon}
                            style={{
                                WebkitMask: "url(/svg/ref_system.svg) center / contain no-repeat",
                                mask: "url(/svg/ref_system.svg) center / contain no-repeat",
                            }}
                            aria-hidden="true"
                        />
                        Реферальная система
                    </span>
                    {!isMoneyReferral ? (
                        <div className={styles.referralIntro}>
                            <span className={styles.referralDescription}>
                                Приглашайте друзей и получайте бонусные дни.
                            </span>
                            <span className={styles.referralBonus}>
                                <span className={styles.referralAccent}>1</span> месяц друга ={" "}
                                <span className={styles.referralAccent}>5 дней</span> для вас.
                            </span>
                            <span className={styles.referralDescription}>
                                Достаточно 6 активных друзей, чтобы пользоваться VPN бесплатно.
                            </span>
                        </div>
                    ) : null}
                </div>
                {!isMoneyReferral ? (
                    <span
                        className={styles.referralTextIcon}
                        style={{
                            height: referralTextIconHeight ?? undefined,
                            width: referralTextIconHeight
                                ? Math.ceil(referralTextIconHeight * REF_TEXT_ICON_ASPECT_RATIO)
                                : undefined,
                            WebkitMask: "url(/svg/ref_text.svg) center top / contain no-repeat",
                            mask: "url(/svg/ref_text.svg) center top / contain no-repeat",
                        }}
                        aria-hidden="true"
                    />
                ) : null}
            </div>

            <span className={styles.referralDivider} aria-hidden="true" />

            <div className={styles.referralDetails}>
                <div className={`flex-col ${styles.referralSection}`}>
                    <span><b>Ваша пригласительная ссылка</b></span>
                    <div className={styles.copyBlock}>
                        <button
                            className={styles.copyButton}
                            disabled={!cabinetData.ref_link}
                            onClick={copyToClipboard}
                            type="button"
                        >
                            <span className="font09">
                                {isLoading ? "Загрузка..." : cabinetData.ref_link || "Ссылка недоступна"}
                            </span>
                            <svg className={styles.copyIcon}>
                                <use href="#icon-rect"></use>
                            </svg>
                        </button>
                        <div className="flex align-center grey">
                            <span className={styles.copyHint}>Нажмите, чтобы скопировать</span>
                        </div>
                    </div>
                </div>

                <span className={styles.referralDivider} aria-hidden="true" />

                <div className={`flex w100 ${styles.stats}`}>
                    <div className="flex w100">
                        <div className={`flex-col flex-center w100 ${styles.statsItemLeft}`}>
                            <span>Приглашено</span>
                            <span className={styles.statsValue}>
                                {isLoading ? "..." : cabinetData.ref_added}
                            </span>
                        </div>
                        <div className={`flex-col flex-center w100 ${styles.statsItemRight}`}>
                            <span>Активные</span>
                            <span className={styles.statsValue}>
                                {isLoading ? "..." : cabinetData.ref_active_added}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {isMoneyReferral ? (
                <div className={`flex-col gap05 ${styles.moneyBlock}`}>
                    <div className="flex-between align-center gap1">
                        <span>Ваш процент</span>
                        <span className={styles.moneyValue}>
                            {isLoading ? "..." : `${cabinetData.ref_percent}%`}
                        </span>
                    </div>
                    <div className="flex-between align-center gap1">
                        <span>Всего заработано</span>
                        <span className={styles.moneyValue}>
                            {isLoading ? "..." : `${cabinetData.ref_total} ₽`}
                        </span>
                    </div>
                    <div className={styles.balanceBlock}>
                        <div className="flex-between align-center gap1">
                            <span className={styles.balanceLabel}>Баланс</span>
                            <span className={styles.balanceValue}>
                                {isLoading ? "..." : `${cabinetData.ref_balance} ₽`}
                            </span>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function ActivateDaysBlock({
    cabinetData,
    isLoading,
    setCabinetData,
}: {
    cabinetData: CabinetData;
    isLoading: boolean;
    setCabinetData: Dispatch<SetStateAction<CabinetData>>;
}) {
    const [modalVisible, setModalVisible] = useState(false);
    const { shouldShowNavigation, setBottomActions } = useNavigationHeader() as {
        shouldShowNavigation: boolean;
        setBottomActions: Dispatch<SetStateAction<ReactNode | null>>;
    };
    const isMoneyReferral = cabinetData.ref_type === "money";

    const activateDays = () => {
        setCabinetData((current) => ({
            ...current,
            days_balance: 0,
        }));
        setModalVisible(false);
    };

    const activateDaysButton = useMemo(() => {
        if (isMoneyReferral) {
            return null;
        }

        return (
            <BaseButton
                className={styles.activateDaysButton}
                onClick={() => setModalVisible(true)}
                type="button"
            >
                <span className={styles.activateDaysContent}>
                    <span className={styles.activateDaysMain}>
                        <span
                            className={styles.activateDaysIcon}
                            style={{
                                WebkitMask: "url(/svg/activate_days.svg) center / contain no-repeat",
                                mask: "url(/svg/activate_days.svg) center / contain no-repeat",
                            }}
                            aria-hidden="true"
                        />
                        <span>Активировать дни</span>
                    </span>
                    <span className={styles.activateDaysStatus}>
                        <span className={styles.activateDaysAvailable}>Доступно</span>
                        <span className={styles.daysBalanceValue}>{isLoading ? "..." : cabinetData.days_balance}</span>
                    </span>
                </span>
            </BaseButton>
        );
    }, [cabinetData.days_balance, isLoading, isMoneyReferral]);

    useEffect(() => {
        if (!shouldShowNavigation || !activateDaysButton) {
            setBottomActions(null);
            return undefined;
        }

        setBottomActions(activateDaysButton);

        return () => {
            setBottomActions(null);
        };
    }, [activateDaysButton, setBottomActions, shouldShowNavigation]);

    if (isMoneyReferral) {
        return null;
    }

    return (
        <>
            {!shouldShowNavigation ? (
                <div className="menu-appear" style={getAppearStyle()}>
                    {activateDaysButton}
                </div>
            ) : null}

            <div className={classNames(styles.modalOverlay, modalVisible && styles.modalOverlayActive)}>
                <div className={`flex-col gap05 pad1 ${styles.modalWindow}`}>
                    <h2 className={`font11 ${styles.modalTitle}`}>
                        Активировать бесплатные дни?
                    </h2>
                    <p className="font08">
                        При активации бонусных дней доступ к VPN продлится до <u><span>31.01.25</span></u>.
                        Активировать сейчас?
                    </p>
                    <div className={`flex-between gap1 ${styles.modalActions}`}>
                        <button
                            className={`${styles.modalConfirm} ${styles.modalAction}`}
                            onClick={activateDays}
                            type="button"
                        >
                            Активировать
                        </button>
                        <button
                            className={styles.modalCancel}
                            onClick={() => setModalVisible(false)}
                            type="button"
                        >
                            <span>Отмена</span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default function Cabinet() {
    const { cabinetData, cabinetStatus, setCabinetData } = useAppData() as CabinetAppData;

    const { isLoading, error } = cabinetStatus;

    return (
        <div className="flex-col gap1">
            <div className={styles.cabinetHeaderSection}>
                <div className={styles.cabinetHeader}>
                    <h2 className="page-header-title">Кабинет</h2>
                    <span className={styles.cabinetSubtitle}>Управляйте аккаунтом и получайте бонусы</span>
                </div>

                <CredentialsBlock
                    cabinetData={cabinetData}
                    isLoading={isLoading}
                />
            </div>

            <div className="flex-col gap1">
                {error ? (
                    <div className="default_block default_block_padded menu-appear" style={getAppearStyle()}>
                        <span>{error}</span>
                    </div>
                ) : null}

                <RefBlock
                    cabinetData={cabinetData}
                    isLoading={isLoading}
                />

                <ActivateDaysBlock
                    cabinetData={cabinetData}
                    isLoading={isLoading}
                    setCabinetData={setCabinetData}
                />
            </div>
        </div>
    );
}
